/**
 * 「这一行能不能退款」的判据 —— **唯一实现**（`DR-066` §5 · `AC-B5.12`）。
 *
 * ## 🔴 为什么抽出来
 *
 * 修前这条判据只写在 `HomePage` 的 JSX 里（`t.parent.kind !== Transfer && net < 0`），
 * 于是「流水页也要能退款」（负责人 2026-10-28 第 6 条）时，只能在第二处再写一遍 ——
 * 那是同一事实的两份实现（铁律 24），而且两份迟早会分叉（一处补了 `parentId` 判断、
 * 另一处没补），表现是"首页能退、流水页点了报 400"。
 *
 * ## 判据（三条**同时**成立）
 *
 * | 条件 | 含义 |
 * |---|---|
 * | `parent.kind !== Transfer` | 转账是资金搬运，没有"被冲减的消费"；服务端 `checkRefundParent` 也会拦 |
 * | `(parent.net ?? 0) < 0` | **净流出**才是支出（工资条那种多笔"各腿相加"没有语义，只有 `net` 有定义） |
 * | `parentId === null` | 本笔自己**不是退款** —— 退款不能再被退，否则会形成冲减链 |
 *
 * 🔴 全部读**服务端派生好的字段**（`parent.*` / `parentId`），端侧不自己按腿判形态。
 * 🔴 不按 `single` / `multi` 筛：一笔**拆分开的支出**同样可能整笔被退 ——
 * 退款冲的是"这笔消费"，不是某一条腿。
 */

import {
  LegDirection,
  ParentRowKind,
  type TransactionLegView,
  type TransactionRowView,
} from '@jiaying/shared';

/** 是否需要 `canRefund` 判定所需的最小投影 */
export type RefundCandidate = Pick<TransactionRowView, 'parentId' | 'parent' | 'refunds'>;

/** 这一行能不能发起退款（`DR-066` §5 · `AC-B5.12`）。唯一实现。 */
export function canRefund(row: RefundCandidate): boolean {
  // ① 转账不是消费
  if (row.parent.kind === ParentRowKind.Transfer) return false;
  // ② 必须是净流出（净额为 0 或为正 = 不是支出）
  if ((row.parent.net ?? 0) >= 0) return false;
  // ③ 本笔不能自己就是退款（防冲减链）
  if (row.parentId !== null) return false;
  /**
   * ④ 🆕 批 `120`（`DR-069` D6）：**已经全额退完的不再给退款入口**。
   *
   * 修前只判"净流出"，于是一笔 175.50 已退清的行仍然挂着「退款」按钮 ——
   * 点进去填任何金额都是二次退款（支出分类会被冲成负、或者被算成收入）。
   * 判据用**原额绝对值**（`-net`），不用 `refundHint` 的文案。
   *
   * ⚠️ `?? null` 不是多余：本函数还会吃到**旧响应**（缓存里没有 `refunds` 键）与
   * 手写夹具 —— `undefined !== null` 会让下面的 `.amount` 直接抛（本批实测踩到）。
   */
  const refunded = row.refunds ?? null;
  if (refunded !== null && refunded.amount >= -(row.parent.net ?? 0)) return false;
  return true;
}

/**
 * 还能退多少（`DR-069` D6）—— 退款页「最多可退」的唯一来源。
 *
 * `原额 − 已退`，负数一律归 0（数据异常时不显示负的"最多可退"）。
 */
export function refundableAmount(row: RefundCandidate): number {
  const net = row.parent.net ?? 0;
  if (net >= 0) return 0;
  const left = -net - (row.refunds?.amount ?? 0);
  return left > 0 ? left : 0;
}

/**
 * 退款草稿要预填的**原支出分类** —— 唯一实现（批 `119`）。
 *
 * ## 🔴 为什么退款腿必须挂**支出**分类（这是本函数存在的全部理由）
 *
 * 退款的形态判据在内核 `isRefundTxn()`（`nucleus/split.ts`）：
 *
 * ```
 * 计入收支的腿全部是 in  +  至少一条挂**支出**分类  ⇒  才算退款
 * ```
 *
 * 挂**收入**分类的 `in` 腿会被判成"一笔普通收入" ⇒ 服务端 `bindRefundToParent()`
 * 直接 400（`parentId 只能用于退款（in 腿 + 挂原支出分类）`）。
 *
 * ⚠️ 修前端侧没有这个意识：退款表单方向锁 `in`、分类选择器按方向筛成**收入分类**
 * ⇒ 原支出分类在列表里根本不存在，用户只能不选或选一个收入分类，**两条路都 400**
 * （负责人 2026-10-29 反馈「退款无法退款…收入没有原来支出的分类，故报错」）。
 *
 * ## 取值口径
 *
 * 退款冲的是**整笔消费**（不是某一条腿）⇒ 多腿拆分时可能有好几个分类。
 * 这里取**第一条挂了分类的支出腿**作为预填**建议值**（用户在表单里仍可改）——
 * 不做"拆分退款要按比例分"的推断：那会让一笔退款变成多腿，而退款只允许单腿。
 */
export function refundCategoryId(row: Pick<TransactionRowView, 'legs'>): string | null {
  const hit = row.legs.find(
    (l) => l.direction === LegDirection.Out && l.categoryId !== null,
  );
  return hit?.categoryId ?? null;
}

// ═══════════════════ 🆕 批 154：拆分项的**单项退款** ═══════════════════

/**
 * 这一条腿**所属分类**还能退多少（原币整数分）—— 单项退款的「最多可退」唯一来源。
 *
 * ```
 * 剩余 = 该腿金额 − 该分类已退（服务端下发的 refunds.byCategory）
 * ```
 *
 * 🔴 不能拿 `refundableAmount(row)` 代替：那是**整笔**的剩余。
 * 一笔「食品 120 + 日用 80」在退过日用 80 之后，整笔剩余是 120，
 * 但**食品这一项**的剩余是 120、**日用这一项**的剩余是 0 —— 用整笔数算会让日用还能被再退一次。
 */
export function refundableLegCents(
  row: Pick<TransactionRowView, 'refunds'>,
  leg: Pick<TransactionLegView, 'categoryId' | 'amount'>,
): number {
  if (leg.categoryId === null) return 0;
  const refunded = row.refunds?.byCategory?.[leg.categoryId] ?? 0;
  const left = leg.amount - refunded;
  return left > 0 ? left : 0;
}

/**
 * 这一条腿能不能**单独**发起退款（🆕 批 `154`）。
 *
 * 判据（**全部**成立）：
 *
 * | # | 条件 | 为什么 |
 * |---|---|---|
 * | 1 | `canRefund(row)` | 整笔层面本来就不可退的（收入 / 转账 / 已是退款 / 已全额退完）一律不谈 |
 * | 2 | 腿是 `out` | 退款冲的是**花出去的那一项**；`in` 腿是收入或上一笔退款 |
 * | 3 | 腿挂了**支出分类** | 退款的冲减腿必须挂支出分类（`isRefundTxn` 的判据）；无分类的腿（手续费 / 转账腿）没有"项"可言 |
 * | 4 | 该腿不是「不计收支」 | 那类腿本来就不进报表，冲减它没有意义 |
 * | 5 | 该分类剩余 > 0 | 已经退完的项不再给入口（与整笔的 `canRefund` 同一条纪律） |
 *
 * 🔴 不做"按比例分摊"的推断：退款冲的是**某一项**，金额由用户填（预填该项剩余）。
 */
export function canRefundLeg(
  row: Pick<TransactionRowView, 'parentId' | 'parent' | 'refunds'>,
  leg: Pick<TransactionLegView, 'direction' | 'categoryId' | 'amount' | 'excludeFromStats'>,
): boolean {
  if (!canRefund(row)) return false;
  if (leg.direction !== LegDirection.Out) return false;
  if (leg.categoryId === null) return false;
  if (leg.excludeFromStats) return false;
  return refundableLegCents(row, leg) > 0;
}
